import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import clsx from 'clsx'

import { DashedAvatarRing, DropdownTriangle, RemoveUser } from '@/assets/icons'
import { MenuContent, MenuItemGroup, MenuSeparator } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { MenuItemContent } from '@/components/menu/MenuItemContent'
import { getMenuItemClassName } from '@/components/menu/menu-item-styles'
import { Tag } from '@/components/tag/Tag'
import type { MatterAssignableRole } from './schemas'
import type { MatterCollaborator } from './types'

const kMemberRoles: { value: MatterAssignableRole; label: string }[] = [
  { value: 'collaborator', label: 'Contributor' },
  { value: 'editor', label: 'Editor' },
]

interface MemberRowProps {
  displayName: string
  email: string
  isOwnerRow: boolean
  isCurrentUser: boolean
  canRemove: boolean
  canTransfer: boolean
  isPendingAcceptance: boolean
  onRemove: () => void
  onTransfer: () => void
  matterRole?: MatterCollaborator['matterRole']
  canChangeRole?: boolean
  roleChanging?: boolean
  actionsDisabled?: boolean
  onChangeRole?: (role: MatterAssignableRole) => void
}

function memberInitials(displayName: string): string {
  return displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase()
}

export function MemberRow({
  displayName,
  email,
  isOwnerRow,
  isCurrentUser,
  canRemove,
  canTransfer,
  isPendingAcceptance,
  onRemove,
  onTransfer,
  matterRole,
  canChangeRole = false,
  roleChanging = false,
  actionsDisabled = false,
  onChangeRole,
}: MemberRowProps) {
  const currentRole = matterRole === 'editor' ? 'editor' : 'collaborator'
  const roleLabel = isOwnerRow ? 'Owner' : matterRole === 'editor' ? 'Editor' : 'Contributor'
  const nameText = isCurrentUser ? `${displayName} (you)` : displayName

  return (
    <div className="flex items-center gap-3 border-b border-brand-neutral-200 px-6 py-2">
      <div
        className={clsx(
          'relative flex size-8 shrink-0 items-center justify-center rounded-full font-medium font-rethink-sans',
          isPendingAcceptance ? 'text-true-gray-400' : 'bg-brand-neutral-200 text-brand-neutral-700'
        )}
      >
        {isPendingAcceptance && <DashedAvatarRing className="absolute inset-0 size-full" aria-hidden />}
        {memberInitials(displayName)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p title={nameText} className="min-w-0 truncate text-sm font-medium text-brand-neutral-950">
            {nameText}
          </p>
          {isPendingAcceptance && (
            <Tag variant="tonal" color="Black">
              Pending
            </Tag>
          )}
        </div>
        <p title={email} className="truncate text-xs text-brand-neutral-800">
          {email}
        </p>
      </div>
      {canRemove || canTransfer || canChangeRole ? (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger
            aria-label={`Manage ${displayName}: ${roleLabel}`}
            aria-busy={roleChanging}
            className="flex shrink-0 items-center rounded-md py-1 pl-1.5 pr-0.5 text-xs font-medium text-brand-neutral-800 hover:bg-brand-neutral-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 data-[state=open]:bg-brand-neutral-100"
          >
            {roleLabel}
            <DropdownTriangle className="size-4" aria-hidden />
          </DropdownMenu.Trigger>
          <MenuContent align="end" sideOffset={4} className="min-w-[200px]">
            {(canTransfer || canChangeRole) && (
              <MenuItemGroup>
                {canTransfer && <MenuItem label="Owner" onSelect={onTransfer} disabled={actionsDisabled} />}
                <DropdownMenu.RadioGroup
                  aria-label="Member role"
                  className="flex flex-col gap-0.5"
                  value={currentRole}
                  onValueChange={(value) => {
                    if ((value === 'collaborator' || value === 'editor') && value !== currentRole) onChangeRole?.(value)
                  }}
                >
                  {kMemberRoles.map(({ value, label }) => {
                    if (!canChangeRole && value !== currentRole) return null
                    const selected = value === currentRole
                    return (
                      <DropdownMenu.RadioItem
                        key={value}
                        value={value}
                        disabled={actionsDisabled}
                        className={getMenuItemClassName({ disabled: actionsDisabled, selected })}
                        data-selected={selected ? '' : undefined}
                      >
                        <MenuItemContent label={label} selected={selected} disabled={actionsDisabled} />
                      </DropdownMenu.RadioItem>
                    )
                  })}
                </DropdownMenu.RadioGroup>
              </MenuItemGroup>
            )}
            {(canTransfer || canChangeRole) && canRemove && <MenuSeparator />}
            {canRemove && (
              <MenuItemGroup>
                <MenuItem label="Remove member" variant="destructive" icon={RemoveUser} onSelect={onRemove} disabled={actionsDisabled} />
              </MenuItemGroup>
            )}
          </MenuContent>
        </DropdownMenu.Root>
      ) : (
        <span className="shrink-0 px-1.5 py-1 text-xs font-medium text-brand-neutral-800">{roleLabel}</span>
      )}
    </div>
  )
}
